import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Star, MapPin, Car, Filter } from "lucide-react";
import Seo from "../components/Seo";
import VerifiedBadge from "../components/VerifiedBadge";

const TRANSMISSIONS = ["any", "manual", "auto"];
const CAR_TYPES = ["any", "sedan", "suv", "hatchback"];
const EQUIPMENT_OPTS = ["any", "Cars", "Motorcycles", "Buses", "Trucks", "Trailers", "Commercial"];

export default function TrainerSearch() {
  const [params, setParams] = useSearchParams();
  const [zones, setZones] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);

  const zone = params.get("zone") || "any";
  const transmission = params.get("transmission") || "any";
  const car_type = params.get("car_type") || "any";
  const equipment = params.get("equipment") || "any";
  const sort = params.get("sort") || "rating";
  const q = params.get("q") || "";

  useEffect(() => { api.get("/zones").then(r => setZones(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const qs = {};
    if (zone !== "any") qs.zone = zone;
    if (transmission !== "any") qs.transmission = transmission;
    if (car_type !== "any") qs.car_type = car_type;
    // Only pass `sort` to the backend for keys it recognises. New client-side
    // sorts (price_desc, sessions, newest) are applied after fetch.
    if (["rating", "price"].includes(sort)) qs.sort = sort;
    api.get("/trainers", { params: qs }).then(r => {
      let data = r.data;
      if (equipment !== "any") {
        data = data.filter(t => (t.equipment || []).includes(equipment));
      }
      if (q.trim()) {
        const needle = q.trim().toLowerCase();
        data = data.filter(t =>
          (t.name || "").toLowerCase().includes(needle) ||
          (t.zone || "").toLowerCase().includes(needle) ||
          (t.city || "").toLowerCase().includes(needle)
        );
      }
      // Client-side sort for the extended options.
      const num = (x) => Number(x) || 0;
      if (sort === "price_desc") data = [...data].sort((a, b) => num(b.hourly_rate) - num(a.hourly_rate));
      else if (sort === "sessions") data = [...data].sort((a, b) => num(b.sessions_completed) - num(a.sessions_completed));
      else if (sort === "newest") data = [...data].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setTrainers(data);
    }).finally(() => setLoading(false));
  }, [zone, transmission, car_type, equipment, sort, q]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "any" || !v) next.delete(k); else next.set(k, v);
    setParams(next);
  };

  const searchJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: trainers.slice(0, 10).map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://driveterrain.ca/trainers/${t.user_id || t.id}`,
      name: t.name,
    })),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="trainer-search-page">
      <Seo
        title="Find a Driving Instructor Near You – DriveTerrain"
        description="Browse verified driving instructors by province, city, transmission (auto/manual) and hourly rate. Real reviews, instant booking, secure CAD checkout."
        keywords="find driving instructor, driving school near me, book driving lesson, manual transmission lessons, automatic lessons, canadian driving school"
        canonical="/trainers"
        jsonLd={trainers.length ? searchJsonLd : undefined}
      />
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">Marketplace</div>
        <h1 className="text-4xl md:text-5xl font-display font-semibold tracking-tight mt-1">Find your trainer</h1>
        <p className="text-muted-foreground mt-2">Filter by zone, transmission, and vehicle type.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3 space-y-6 lg:sticky lg:top-20 self-start">
          <div className="border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-primary" />
              <div className="text-xs uppercase tracking-[0.2em] font-bold">Filters</div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Zone</label>
                <Select value={zone} onValueChange={(v) => setParam("zone", v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="filter-zone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">All zones</SelectItem>
                    {zones.map(z => <SelectItem key={z.zone_id} value={z.name}>{z.name} · {z.city}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Transmission</label>
                <Select value={transmission} onValueChange={(v) => setParam("transmission", v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="filter-transmission"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRANSMISSIONS.map(t => <SelectItem key={t} value={t}>{t === "any" ? "Any" : t.charAt(0).toUpperCase()+t.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Car type</label>
                <Select value={car_type} onValueChange={(v) => setParam("car_type", v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="filter-cartype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CAR_TYPES.map(t => <SelectItem key={t} value={t}>{t === "any" ? "Any" : t.charAt(0).toUpperCase()+t.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Equipment</label>
                <Select value={equipment} onValueChange={(v) => setParam("equipment", v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="filter-equipment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_OPTS.map(t => <SelectItem key={t} value={t}>{t === "any" ? "Any" : t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Sort by</label>
                <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="filter-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rating">Top rated</SelectItem>
                    <SelectItem value="price">Price: low → high</SelectItem>
                    <SelectItem value="price_desc">Price: high → low</SelectItem>
                    <SelectItem value="sessions">Most sessions completed</SelectItem>
                    <SelectItem value="newest">Newest trainers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <Input
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder="Search by trainer name or city…"
              className="rounded-sm sm:max-w-xs"
              data-testid="trainer-search-input"
            />
            <div className="text-sm text-muted-foreground" data-testid="result-count">{trainers.length} trainers found</div>
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="border border-border bg-card h-56 animate-pulse" />)}
            </div>
          ) : trainers.length === 0 ? (
            <div className="border border-border bg-card p-12 text-center text-muted-foreground" data-testid="empty-trainers">
              No trainers match these filters. Try widening your search.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {trainers.map(t => (
                <Link key={t.user_id} to={`/trainers/${t.user_id}`} className="border border-border bg-card p-5 hover:-translate-y-1 transition-all duration-200 group" data-testid={`trainer-card-${t.user_id}`}>
                  <div className="flex gap-4">
                    <div className="w-20 h-20 bg-muted overflow-hidden flex-shrink-0">
                      {t.photo_url && <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="font-display text-lg font-medium truncate">{t.name}</div>
                        {t.verified && <VerifiedBadge size="sm" showLabel={false} testId={`verified-${t.user_id}`} />}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="w-3 h-3" /> {t.zone}</div>
                      <div className="flex items-center gap-1 text-sm mt-1" data-testid={`card-rating-${t.user_id}`}>
                        {t.review_count > 0 && t.rating != null ? (
                          <>
                            <Star className="w-3 h-3 fill-primary text-primary" />
                            <span className="font-medium">{Number(t.rating).toFixed(1)}</span>
                            <span className="text-muted-foreground">· {t.review_count} review{t.review_count === 1 ? "" : "s"}</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground italic">New instructor</span>
                        )}
                        <span className="text-muted-foreground">· {t.sessions_completed || 0} sessions</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Hourly · CAD</div>
                      <div className="font-display text-2xl font-semibold">${t.hourly_rate}</div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      <div className="flex items-center gap-1 justify-end"><Car className="w-3 h-3" /> {t.transmissions?.join(" · ")}</div>
                      <div className="mt-1">{t.car_types?.join(" · ")}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
